import { useState, useEffect } from 'react'
import { X, AlertCircle, ChevronDown } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { OrgInviteService } from '@/api/generated/services/OrgInviteService'
import { WorkspaceService } from '@/api/generated/services/WorkspaceService'
import type { OrgInviteCreateRequest } from '@/api/generated/models/OrgInviteCreateRequest'
import type { OrgInviteCreateResponse } from '@/api/generated/models/OrgInviteCreateResponse'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'

interface MemberInviteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function MemberCreateModal({
  isOpen,
  onClose,
  onSuccess
}: MemberInviteModalProps) {
  const [emailsInput, setEmailsInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [inviteResponse, setInviteResponse] = useState<OrgInviteCreateResponse | null>(null)

  // Workspace selection state
  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([])
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([])
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false)
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false)

  const { showError, showSuccess } = useAlert()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Fetch workspaces when modal opens
  useEffect(() => {
    if (isOpen && selectedWorkspace?.org_id) {
      fetchWorkspaces()
    }
  }, [isOpen, selectedWorkspace?.org_id])

  const fetchWorkspaces = async () => {
    if (!selectedWorkspace?.org_id) return

    setIsLoadingWorkspaces(true)
    try {
      const response = await WorkspaceService.appsWorkspaceViewsListWorkspaces({
        orgId: selectedWorkspace.org_id,
        includeUnavailable: false,
        page: 1,
        perPage: 100
      })

      const fetchedWorkspaces = response.data || []
      setWorkspaces(fetchedWorkspaces)

      // Select all workspaces by default
      setSelectedWorkspaceIds(fetchedWorkspaces.map(ws => ws.id))
    } catch (error) {
      console.error('Failed to fetch workspaces:', error)
      handleApiError(error, showError, '워크스페이스 목록을 불러오는데 실패했습니다')
    } finally {
      setIsLoadingWorkspaces(false)
    }
  }

  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaceIds(prev => {
      if (prev.includes(workspaceId)) {
        return prev.filter(id => id !== workspaceId)
      } else {
        return [...prev, workspaceId]
      }
    })
  }

  const handleSelectAllWorkspaces = () => {
    if (selectedWorkspaceIds.length === workspaces.length) {
      // Deselect all
      setSelectedWorkspaceIds([])
    } else {
      // Select all
      setSelectedWorkspaceIds(workspaces.map(ws => ws.id))
    }
  }

  const parseEmails = (input: string): string[] => {
    // Split by comma, semicolon, or newline and clean up
    return input
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0)
  }

  const validateEmails = (emails: string[]): { valid: string[], invalid: string[] } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const valid: string[] = []
    const invalid: string[] = []

    emails.forEach(email => {
      if (emailRegex.test(email)) {
        valid.push(email)
      } else {
        invalid.push(email)
      }
    })

    return { valid, invalid }
  }

  const handleSubmit = async () => {
    if (!emailsInput.trim()) {
      showError('이메일을 입력해주세요.')
      return
    }

    if (!selectedWorkspace?.org_id) {
      showError('조직 정보를 찾을 수 없습니다.')
      return
    }

    const emails = parseEmails(emailsInput)
    if (emails.length === 0) {
      showError('올바른 이메일을 입력해주세요.')
      return
    }

    const { valid, invalid } = validateEmails(emails)

    if (invalid.length > 0) {
      showError(`잘못된 이메일 형식이 있습니다: ${invalid.join(', ')}`)
      return
    }

    if (valid.length === 0) {
      showError('올바른 이메일이 없습니다.')
      return
    }

    setIsLoading(true)

    try {
      const inviteRequest: OrgInviteCreateRequest = {
        org_id: selectedWorkspace.org_id,
        emails: valid,
        invited_workspaces: selectedWorkspaceIds.length > 0 ? selectedWorkspaceIds : null
      }

      const response = await OrgInviteService.appsOrganizationViewsCreateOrgInvite({
        requestBody: inviteRequest
      })

      // Store response for displaying results
      setInviteResponse(response)

      // Show success message from API response
      showSuccess(response.message)

      // If all invites succeeded, close modal and trigger success callback
      if (response.failed_count === 0) {
        setTimeout(() => {
          handleClose()
          onSuccess?.()
        }, 1500)
      }
      // If there are failures, keep modal open to show failed invites list
      // Don't call onSuccess to prevent parent from closing modal
    } catch (error: any) {
      console.error('Failed to send invite:', error)
      handleApiError(error, showError, '멤버 초대 전송에 실패했습니다')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmailsInput('')
    setInviteResponse(null)
    setWorkspaces([])
    setSelectedWorkspaceIds([])
    setIsWorkspaceDropdownOpen(false)
    onClose()
  }

  const emails = parseEmails(emailsInput)
  const { valid } = validateEmails(emails)
  const hasValidEmails = valid.length > 0

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              멤버 초대
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Help Text */}
            <div>
              <p
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  lineHeight: designTokens.typography.fontSize['body-small'][1].lineHeight,
                  color: designTokens.colors.gray[4]
                }}
              >
                여러 이메일은 쉼표(,) 또는 줄바꿈으로 구분해주세요.
              </p>
              <p
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  lineHeight: designTokens.typography.fontSize['body-small'][1].lineHeight,
                  color: designTokens.colors.gray[4]
                }}
              >
                한번에 최대 50개까지 초대가 가능합니다.
              </p>
            </div>

            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Workspace Selection - Dropdown Style */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        워크스페이스 선택
                      </label>
                    </div>
                    <div className="flex-1 relative">
                      {isLoadingWorkspaces ? (
                        <div
                          className="w-full px-3 py-2 border rounded"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-small'][0],
                            color: designTokens.colors.gray[4],
                            borderColor: designTokens.colors.gray[1]
                          }}
                        >
                          워크스페이스 목록을 불러오는 중...
                        </div>
                      ) : (
                        <>
                          {/* Dropdown Trigger Button */}
                          <button
                            type="button"
                            onClick={() => setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen)}
                            disabled={inviteResponse !== null}
                            className="w-full px-3 py-2 border rounded flex items-center justify-between hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              borderColor: designTokens.colors.gray[2],
                              backgroundColor: inviteResponse !== null ? designTokens.colors.gray[0] : 'white'
                            }}
                          >
                            <span
                              style={{
                                color: selectedWorkspaceIds.length > 0 ? designTokens.colors.gray[6] : designTokens.colors.gray[4]
                              }}
                            >
                              {selectedWorkspaceIds.length === 0
                                ? '워크스페이스를 선택하세요'
                                : selectedWorkspaceIds.length === workspaces.length
                                ? `전체 선택 (${workspaces.length}개)`
                                : `${selectedWorkspaceIds.length}개 선택됨`}
                            </span>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${isWorkspaceDropdownOpen ? 'rotate-180' : ''}`}
                              style={{ color: designTokens.colors.gray[5] }}
                            />
                          </button>

                          {/* Dropdown Menu */}
                          {isWorkspaceDropdownOpen && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10 max-h-[300px] overflow-y-auto"
                              style={{
                                borderColor: designTokens.colors.gray[1]
                              }}
                            >
                              {/* Select All Option */}
                              <div
                                className="flex items-center gap-2 p-3 hover:bg-gray-50 transition-colors border-b cursor-pointer"
                                style={{ borderColor: designTokens.colors.gray[1] }}
                                onClick={handleSelectAllWorkspaces}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedWorkspaceIds.length === workspaces.length && workspaces.length > 0}
                                  onChange={handleSelectAllWorkspaces}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 rounded border-gray-300"
                                  style={{
                                    accentColor: designTokens.colors.primary.DEFAULT
                                  }}
                                />
                                <span
                                  style={{
                                    fontFamily: designTokens.typography.fontFamily.sans[0],
                                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                                    fontWeight: 600,
                                    color: designTokens.colors.gray[6]
                                  }}
                                >
                                  전체 선택 ({selectedWorkspaceIds.length}/{workspaces.length})
                                </span>
                              </div>

                              {/* Workspace List */}
                              {workspaces.length === 0 ? (
                                <div
                                  className="p-3 text-center"
                                  style={{
                                    fontFamily: designTokens.typography.fontFamily.sans[0],
                                    fontSize: designTokens.typography.fontSize['body-small'][0],
                                    color: designTokens.colors.gray[4]
                                  }}
                                >
                                  워크스페이스가 없습니다
                                </div>
                              ) : (
                                workspaces.map(workspace => (
                                  <div
                                    key={workspace.id}
                                    className="flex items-center gap-2 p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleWorkspaceToggle(workspace.id)}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedWorkspaceIds.includes(workspace.id)}
                                      onChange={() => handleWorkspaceToggle(workspace.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-300"
                                      style={{
                                        accentColor: designTokens.colors.primary.DEFAULT
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontFamily: designTokens.typography.fontFamily.sans[0],
                                        fontSize: designTokens.typography.fontSize['body-medium'][0],
                                        color: designTokens.colors.gray[6]
                                      }}
                                    >
                                      {workspace.name}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Email Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        이메일 목록
                        <span
                          className="ml-1"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <textarea
                        placeholder="초대할 멤버의 이메일 주소를 입력해주세요."
                        value={emailsInput}
                        onChange={(e) => setEmailsInput(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={4}
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight
                        }}
                        disabled={inviteResponse !== null}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Failed Invites List */}
              {inviteResponse && inviteResponse.failed_invites && inviteResponse.failed_invites.length > 0 && (
                <div
                  className="flex flex-col gap-3 p-4 rounded"
                  style={{
                    backgroundColor: designTokens.colors.error.light,
                    border: `1px solid ${designTokens.colors.error.DEFAULT}`
                  }}
                >
                  {/* Failed Invites Header */}
                  <div className="flex items-center gap-2">
                    <AlertCircle
                      className="w-5 h-5"
                      style={{ color: designTokens.colors.error.DEFAULT }}
                      strokeWidth={2}
                    />
                    <h3
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: designTokens.typography.fontSize['body-medium'][0],
                        fontWeight: 600,
                        color: designTokens.colors.error.DEFAULT
                      }}
                    >
                      초대 실패 목록 ({inviteResponse.failed_count}건)
                    </h3>
                  </div>

                  {/* Failed Invites Table */}
                  <div className="flex flex-col gap-2">
                    {inviteResponse.failed_invites.map((failedInvite, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 rounded"
                        style={{
                          backgroundColor: 'white',
                          border: `1px solid ${designTokens.colors.gray[1]}`
                        }}
                      >
                        <div className="flex-1">
                          <div
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              fontWeight: 600,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            {failedInvite.email}
                          </div>
                          <div
                            className="mt-1"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-small'][0],
                              color: designTokens.colors.gray[5]
                            }}
                          >
                            {failedInvite.reason}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              {inviteResponse && inviteResponse.failed_invites && inviteResponse.failed_invites.length > 0 ? (
                // Show only confirm button when there are failed invites
                <Button
                  color="dark"
                  size="medium"
                  onClick={handleClose}
                  className="px-[10px] h-10"
                >
                  확인
                </Button>
              ) : (
                // Show cancel and invite buttons for initial state
                <>
                  <Button
                    color="light"
                    size="medium"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="px-[10px] h-10"
                  >
                    취소
                  </Button>
                  <Button
                    color="dark"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={isLoading || !hasValidEmails}
                    className="px-[10px] h-10"
                  >
                    {isLoading ? '초대 중...' : '초대'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}